import React, { useEffect, useRef, useState, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { MessageCircle, X, Send, FlaskConical, Loader2 } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

function getSessionId() {
  let id = localStorage.getItem("hxr_ai_session");
  if (!id) {
    id = (crypto.randomUUID ? crypto.randomUUID() : `s-${Date.now()}-${Math.random().toString(36).slice(2)}`);
    localStorage.setItem("hxr_ai_session", id);
  }
  return id;
}

const WELCOME = {
  id: "welcome",
  role: "assistant",
  content:
    "Welcome to Hexxium Research. I can answer questions about our research compounds — mechanisms, published findings, purity, and laboratory handling. What would you like to know?\n\nFor laboratory research use only. Not medical advice.",
};

export default function ResearchAssistant() {
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([WELCOME]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [historyLoaded, setHistoryLoaded] = useState(false);
  const scrollRef = useRef(null);
  const sessionId = useRef(getSessionId());

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, open]);

  useEffect(() => {
    if (!open || historyLoaded) return;
    fetch(`${API}/api/ai-chat/history/${sessionId.current}`)
      .then((r) => r.json())
      .then((d) => {
        if (d.messages?.length) setMessages([WELCOME, ...d.messages.map((m, idx) => ({ id: `hist-${idx}`, role: m.role, content: m.content }))]);
      })
      .catch(() => {})
      .finally(() => setHistoryLoaded(true));
  }, [open, historyLoaded]);

  const sendMessage = useCallback(async () => {
    const text = input.trim();
    if (!text || streaming) return;
    setInput("");
    setMessages((prev) => [...prev, { id: `u-${Date.now()}`, role: "user", content: text }, { id: `a-${Date.now()}`, role: "assistant", content: "" }]);
    setStreaming(true);
    try {
      const res = await fetch(`${API}/api/ai-chat/message`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId.current, message: text }),
      });
      if (!res.ok || !res.body) throw new Error("request failed");
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n\n");
        buffer = lines.pop();
        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          const payload = JSON.parse(line.slice(6));
          if (payload.delta) {
            setMessages((prev) => {
              const next = [...prev];
              next[next.length - 1] = { ...next[next.length - 1], content: next[next.length - 1].content + payload.delta };
              return next;
            });
          } else if (payload.error) {
            setMessages((prev) => {
              const next = [...prev];
              next[next.length - 1] = { ...next[next.length - 1], content: payload.error };
              return next;
            });
          }
        }
      }
    } catch {
      setMessages((prev) => {
        const next = [...prev];
        next[next.length - 1] = {
          ...next[next.length - 1],
          content: "Sorry — I couldn't reach the assistant. Please try again in a moment.",
        };
        return next;
      });
    } finally {
      setStreaming(false);
    }
  }, [input, streaming]);

  if (pathname.startsWith("/admin")) return null;

  return (
    <>
      {!open && (
        <button
          data-testid="ai-chat-open-button"
          onClick={() => setOpen(true)}
          aria-label="Open research assistant"
          className="fixed bottom-5 right-5 z-[70] flex h-14 w-14 items-center justify-center rounded-full bg-[#00AEEF] text-white shadow-[0_0_24px_rgba(0,174,239,0.45)] transition-transform duration-200 hover:scale-110"
        >
          <MessageCircle className="h-6 w-6" />
        </button>
      )}

      {open && (
        <div
          data-testid="ai-chat-panel"
          className="fixed bottom-5 right-5 z-[70] flex h-[560px] w-[380px] max-w-[calc(100vw-2rem)] flex-col overflow-hidden rounded-2xl border border-slate-500/30 bg-[#101319] shadow-2xl max-sm:h-[75vh]"
        >
          <div className="flex items-center justify-between border-b border-slate-500/20 bg-gradient-to-r from-[#0c0f14] to-[#131820] px-4 py-3">
            <div className="flex items-center gap-2.5">
              <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-[#00AEEF]/15 text-[#00AEEF]">
                <FlaskConical className="h-5 w-5" />
              </div>
              <div>
                <p className="text-sm font-semibold text-slate-100">Research Assistant</p>
                <p className="text-[11px] text-slate-400">Powered by Claude · Research use only</p>
              </div>
            </div>
            <button
              data-testid="ai-chat-close-button"
              onClick={() => setOpen(false)}
              aria-label="Close chat"
              className="rounded-md p-1.5 text-slate-400 transition-colors hover:bg-white/5 hover:text-slate-200"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          <div ref={scrollRef} data-testid="ai-chat-messages" className="flex-1 space-y-3 overflow-y-auto px-4 py-4">
            {messages.map((m) => (
              <div key={m.id} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div
                  className={`max-w-[85%] whitespace-pre-wrap rounded-xl px-3.5 py-2.5 text-[13px] leading-relaxed ${
                    m.role === "user"
                      ? "bg-[#00AEEF] text-white"
                      : "border border-slate-500/20 bg-[#181d26] text-slate-200"
                  }`}
                >
                  {m.content || (
                    <span className="flex items-center gap-1.5 text-slate-400">
                      <Loader2 className="h-3.5 w-3.5 animate-spin" /> Thinking…
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>

          <div className="border-t border-slate-500/20 bg-[#0c0f14] p-3">
            <div className="flex items-center gap-2">
              <input
                data-testid="ai-chat-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && sendMessage()}
                placeholder="Ask about a compound…"
                maxLength={2000}
                className="flex-1 rounded-lg border border-slate-500/25 bg-[#131820] px-3.5 py-2.5 text-[13px] text-slate-100 placeholder:text-slate-500 focus:border-[#00AEEF]/60 focus:outline-none"
              />
              <button
                data-testid="ai-chat-send-button"
                onClick={sendMessage}
                disabled={streaming || !input.trim()}
                aria-label="Send message"
                className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-[#00AEEF] text-white transition-opacity disabled:opacity-40"
              >
                <Send className="h-4 w-4" />
              </button>
            </div>
            <p className="mt-2 text-center text-[10px] text-slate-500">
              Educational only · Not medical advice · Compounds are for in-vitro research
            </p>
          </div>
        </div>
      )}
    </>
  );
}
